import React, { lazy, Suspense, useCallback, useMemo } from "react";
import { ACTIONS, STATUS } from "react-joyride";
import type { EventData, TooltipRenderProps } from "react-joyride";
import type { TourStep } from "../../config/onboardingTourConfig";

const Joyride = lazy(() =>
  import("react-joyride").then((mod) => ({ default: mod.Joyride }))
);

/**
 * Design token colors from tailwind.config.js.
 * Kept in sync with the primary/neutral palette defined there.
 */
const COLORS = {
  neutral900: "#18181b",
  neutral500: "#71717a",
} as const;

/**
 * Resolve a `primary` palette shade from the per-brand CSS variables
 * (--color-primary-<shade>, space-separated RGB channels) set in
 * public/index.html / src/index.css and overridden per host by nginx. Keeps the
 * onboarding tour on-brand (green for kiedynabory, blue for hotelb2b) instead of
 * a hardcoded color. Falls back to the kiedynabory green default when the
 * variable is unavailable (SSR/tests). Shade 700 is AA-safe for white text.
 */
function resolvePrimary(shade: number, fallback: string): string {
  if (typeof window === "undefined" || typeof getComputedStyle === "undefined") {
    return fallback;
  }
  const channels = getComputedStyle(document.documentElement)
    .getPropertyValue(`--color-primary-${shade}`)
    .trim();
  return channels ? `rgb(${channels})` : fallback;
}

/**
 * Creates a custom tooltip component that calls onComplete directly on skip/close.
 *
 * react-joyride v3 has a bug: the callback is NOT fired when skipping on the
 * first step (index 0) because `previousStep` is undefined. By wrapping the
 * skip/close button onClick handlers, we ensure onComplete is always called.
 * TODO: remove workaround when react-joyride fixes callback on step 0
 */
function createAccessibleTooltip(
  onComplete: () => void
): React.FC<TooltipRenderProps> {
  const AccessibleTooltip: React.FC<TooltipRenderProps> = ({
    backProps,
    closeProps,
    continuous,
    index,
    isLastStep,
    primaryProps,
    skipProps,
    step,
    tooltipProps,
  }) => {
    const { styles, buttons } = step;
    const hasSkip = buttons.includes("skip");
    const hasBack = buttons.includes("back");

    return (
      <div
        {...tooltipProps}
        role="dialog"
        aria-label="Samouczek"
        style={styles.tooltip}
      >
        <div aria-live="polite">
          {step.title && (
            <h4 style={styles.tooltipTitle}>{step.title}</h4>
          )}
          <div style={styles.tooltipContent}>{step.content}</div>
        </div>
        <div style={styles.tooltipFooter}>
          {hasSkip && !isLastStep && (
            <button
              {...skipProps}
              style={styles.buttonSkip}
              onClick={(e) => {
                skipProps.onClick(e);
                onComplete();
              }}
            >
              {step.locale.skip}
            </button>
          )}
          <div style={styles.tooltipFooterSpacer} />
          {hasBack && index > 0 && (
            <button {...backProps} style={styles.buttonBack}>
              {step.locale.back}
            </button>
          )}
          {continuous && (
            <button {...primaryProps} style={styles.buttonPrimary}>
              {isLastStep ? step.locale.last : step.locale.next}
            </button>
          )}
          {!continuous && (
            <button
              {...closeProps}
              style={styles.buttonClose}
              onClick={(e) => {
                closeProps.onClick(e);
                onComplete();
              }}
            >
              {step.locale.close}
            </button>
          )}
        </div>
      </div>
    );
  };

  return AccessibleTooltip;
}

interface OnboardingTourProps {
  /** Whether the tour should currently be running */
  shouldRunTour: boolean;
  /** Array of tour steps to display */
  steps: TourStep[];
  /** Called when tour is finished or skipped */
  onComplete: () => void;
}

/**
 * Wrapper component for react-joyride.
 * Lazy-loads Joyride only when shouldRunTour is true.
 *
 * Styling matches the design system:
 * - backgroundColor: white
 * - primaryColor: per-brand `primary-700` (AA-safe), resolved at runtime from
 *   the --color-primary-700 CSS variable (kiedynabory green #0a6c4c, hotelb2b blue)
 * - textColor: neutral-900
 * - borderRadius: 8px
 */
const OnboardingTour: React.FC<OnboardingTourProps> = ({
  shouldRunTour,
  steps,
  onComplete,
}) => {
  const handleCallback = useCallback(
    (data: EventData) => {
      const { status, action } = data;
      if (
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED ||
        // react-joyride v3 may fire SKIP/CLOSE action before status transitions,
        // so we also check action to handle mid-tour dismissal reliably.
        action === ACTIONS.SKIP ||
        action === ACTIONS.CLOSE
      ) {
        onComplete();
      }
    },
    [onComplete]
  );

  // Memoize tooltip component to avoid re-creating on every render.
  // react-joyride v3 bug: callback is not fired when skipping on step 0
  // (previousStep is undefined), so we call onComplete directly from the button.
  const tooltipComponent = useMemo(
    () => createAccessibleTooltip(onComplete),
    [onComplete]
  );

  // Brand primary (AA-safe shade 700): green on kiedynabory, blue on hotelb2b.
  // Memoized — the --color-primary-* CSS variables are constant at runtime, so
  // there is no need to re-read getComputedStyle on every render.
  const primaryColor = useMemo(() => resolvePrimary(700, "#0a6c4c"), []);

  if (!shouldRunTour) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <Joyride
        steps={steps}
        run={shouldRunTour}
        continuous
        onEvent={handleCallback}
        options={{
          buttons: ["back", "close", "primary", "skip"],
          showProgress: true,
          primaryColor: primaryColor,
          textColor: COLORS.neutral900,
          backgroundColor: "#ffffff",
          arrowColor: "#ffffff",
          overlayColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 10000,
        }}
        tooltipComponent={tooltipComponent}
        locale={{
          back: "Wstecz",
          close: "Zamknij",
          last: "Zakończ",
          next: "Dalej",
          skip: "Pomiń",
        }}
        styles={{
          tooltip: {
            borderRadius: 8,
            padding: "20px",
          },
          tooltipTitle: {
            fontSize: 16,
            fontWeight: 600,
          },
          tooltipContent: {
            fontSize: 14,
            lineHeight: 1.5,
          },
          buttonPrimary: {
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            padding: "8px 16px",
          },
          buttonBack: {
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            color: primaryColor,
          },
          buttonSkip: {
            fontSize: 13,
            color: COLORS.neutral500,
          },
          buttonClose: {
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            padding: "8px 16px",
          },
        }}
      />
    </Suspense>
  );
};

export default OnboardingTour;
